import { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { useNavigate, useParams } from 'react-router-dom';

import SoftButton from 'components/SoftButton';
import SoftBox from 'components/SoftBox';
import SoftInput from 'components/SoftInput';
import { showSuiErrorSnackbar, showSuiSuccessSnackbar } from 'components/SuiSnackbar';
import SoftTypography from 'components/SoftTypography';

import { useAuth } from 'auth';
import {
  fetchEventByUuid,
  GooglePlacesAutocomplete,
  readCachedEventByUuid,
} from 'event';
import { CategorySelect } from 'profile';
import { type ProfileCategory, type ProfileSkill } from 'profile/profile.api';
import { SkillsAutocomplete } from 'profile/skills-autocomplete';
import { normalizeEndDateTimeForStartChange } from 'lib/datetime-local';
import { useLocale } from 'lib/i18n/client';
import { getTranslations } from 'lib/i18n/dictionary';
import {
  closePositionRequest,
  deletePositionRequest,
  fetchPositionByUuid,
  publishPositionRequest,
  readCachedPositionByUuid,
  updatePositionRequest,
  type PositionBudgetCurrency,
  type PositionStatus,
} from 'position';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY_CODE } from 'currency/currency';

function toDateTimeLocalValue(value: Date): string {
  const localValue = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return localValue.toISOString().slice(0, 16);
}

function toSafeDateTimeLocalValue(value?: string | null): string {
  if (!value) {
    return '';
  }

  const parsedValue = new Date(value);
  if (!Number.isFinite(parsedValue.getTime())) {
    return '';
  }

  return toDateTimeLocalValue(parsedValue);
}

function extractRegionFromCityLabel(cityLabel: string): string {
  const parts = cityLabel
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length < 2) {
    return '';
  }

  return parts.slice(1).join(', ');
}

function EditPosition() {
  const navigate = useNavigate();
  const { uuid = '' } = useParams();
  const { accessToken } = useAuth();
  const locale = useLocale();
  const translations = getTranslations(locale).positionsPage;
  const cachedPosition = uuid.trim() ? readCachedPositionByUuid(uuid) : undefined;

  const [isLoading, setIsLoading] = useState(() => cachedPosition === undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [locationPlaceId, setLocationPlaceId] = useState<string | null>(null);
  const [locationCoordinates, setLocationCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [budgetMinInput, setBudgetMinInput] = useState('');
  const [budgetMaxInput, setBudgetMaxInput] = useState('');
  const [budgetCurrency, setBudgetCurrency] = useState<PositionBudgetCurrency>(DEFAULT_CURRENCY_CODE);
  const [startsAtInput, setStartsAtInput] = useState('');
  const [endsAtInput, setEndsAtInput] = useState('');
  const [skills, setSkills] = useState<ProfileSkill[]>([]);
  const [categories, setCategories] = useState<ProfileCategory[]>([]);
  const [status, setStatus] = useState<PositionStatus>('DRAFT');
  const [eventRegion, setEventRegion] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (errorMessage) {
      showSuiErrorSnackbar(errorMessage);
    }
  }, [errorMessage]);

  const applyPositionDetails = (
    position: NonNullable<ReturnType<typeof readCachedPositionByUuid>>
  ) => {
    setTitle(position.title);
    setDescription(position.description ?? '');
    setLocation(position.location?.label ?? '');
    setLocationPlaceId(position.location?.placeId ?? null);
    setLocationCoordinates(position.location?.coordinates ?? null);
    setBudgetMinInput(
      typeof position.budgetMin === 'number' ? position.budgetMin.toString() : ''
    );
    setBudgetMaxInput(
      typeof position.budgetMax === 'number' ? position.budgetMax.toString() : ''
    );
    setBudgetCurrency(position.budgetCurrency ?? DEFAULT_CURRENCY_CODE);
    setStartsAtInput(toSafeDateTimeLocalValue(position.startsAt));
    setEndsAtInput(toSafeDateTimeLocalValue(position.endsAt));
    setSkills(position.skills ?? []);
    setCategories(position.categories ?? []);
    setStatus(position.status);
  };

  useEffect(() => {
    let isCancelled = false;

    async function loadPosition() {
      if (!uuid.trim()) {
        navigate('/positions', { replace: true });
        return;
      }

      const cachedData = readCachedPositionByUuid(uuid);
      if (!isCancelled && cachedData !== undefined) {
        if (cachedData) {
          applyPositionDetails(cachedData);
          const cachedEvent =
            cachedData.eventUuid?.trim().length
              ? readCachedEventByUuid(cachedData.eventUuid)
              : undefined;
          if (cachedEvent) {
            setEventRegion(extractRegionFromCityLabel(cachedEvent.city?.label ?? ''));
          }
        }
        setIsLoading(false);
      } else if (!isCancelled) {
        setIsLoading(true);
      }

      setErrorMessage('');

      try {
        const position = await fetchPositionByUuid(uuid, accessToken ?? undefined);

        if (!position) {
          if (!isCancelled) {
            setErrorMessage(translations.notFound);
          }
          return;
        }

        if (!isCancelled) {
          applyPositionDetails(position);
        }

        if (position.eventUuid?.trim().length) {
          const parentEvent = await fetchEventByUuid(
            position.eventUuid,
            accessToken ?? undefined
          );
          if (!isCancelled) {
            setEventRegion(extractRegionFromCityLabel(parentEvent?.city?.label ?? ''));
          }
        } else if (!isCancelled) {
          setEventRegion('');
        }
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : translations.loadFailed
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadPosition();

    return () => {
      isCancelled = true;
    };
  }, [accessToken, navigate, translations.loadFailed, translations.notFound, uuid]);

  const handleStartsAtInputChange = (nextValue: string) => {
    setStartsAtInput(nextValue);
    setEndsAtInput((currentEndsAtInput) =>
      normalizeEndDateTimeForStartChange(nextValue, currentEndsAtInput)
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedTitle = title.trim();
    const normalizedDescription = description.trim();
    const normalizedLocation = location.trim();

    if (normalizedTitle.length < 2) {
      setErrorMessage(translations.validationTitleMin);
      return;
    }

    if (normalizedDescription.length > 2000) {
      setErrorMessage(translations.validationDescriptionMax);
      return;
    }

    if (normalizedLocation.length > 128) {
      setErrorMessage(translations.validationLocationMax);
      return;
    }

    const budgetMinValue = budgetMinInput.trim().length
      ? Number(budgetMinInput)
      : null;
    const budgetMaxValue = budgetMaxInput.trim().length
      ? Number(budgetMaxInput)
      : null;

    if (budgetMinValue !== null && (!Number.isFinite(budgetMinValue) || budgetMinValue < 0)) {
      setErrorMessage(translations.validationBudgetMin);
      return;
    }

    if (budgetMaxValue !== null && (!Number.isFinite(budgetMaxValue) || budgetMaxValue < 0)) {
      setErrorMessage(translations.validationBudgetMax);
      return;
    }

    if (budgetMinValue !== null && budgetMaxValue !== null && budgetMaxValue < budgetMinValue) {
      setErrorMessage(translations.validationBudgetRange);
      return;
    }

    const hasStartInput = startsAtInput.trim().length > 0;
    const hasEndInput = endsAtInput.trim().length > 0;

    if (hasStartInput !== hasEndInput) {
      setErrorMessage(translations.validationScheduleTogether);
      return;
    }

    let startsAtIso: string | null = null;
    let endsAtIso: string | null = null;

    if (hasStartInput && hasEndInput) {
      const startsAtDate = new Date(startsAtInput);
      const endsAtDate = new Date(endsAtInput);

      if (!Number.isFinite(startsAtDate.getTime())) {
        setErrorMessage(translations.validationStartInvalid);
        return;
      }

      if (!Number.isFinite(endsAtDate.getTime())) {
        setErrorMessage(translations.validationEndInvalid);
        return;
      }

      if (endsAtDate.getTime() <= startsAtDate.getTime()) {
        setErrorMessage(translations.validationEndAfterStart);
        return;
      }

      startsAtIso = startsAtDate.toISOString();
      endsAtIso = endsAtDate.toISOString();
    }

    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await updatePositionRequest(
        {
          uuid,
          title: normalizedTitle,
          description: normalizedDescription || undefined,
          location: normalizedLocation
            ? {
                label: normalizedLocation,
                placeId: locationPlaceId,
                coordinates: locationCoordinates,
              }
            : null,
          budgetMin: budgetMinValue,
          budgetMax: budgetMaxValue,
          budgetCurrency,
          startsAt: hasStartInput ? startsAtIso : null,
          endsAt: hasEndInput ? endsAtIso : null,
          skills,
          categories,
        },
        accessToken ?? undefined
      );

      navigate('/positions', { replace: true });
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : translations.updateFailed
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!uuid.trim()) {
      return;
    }

    setIsUpdatingStatus(true);
    setErrorMessage('');

    try {
      const updated = await publishPositionRequest(uuid, accessToken ?? undefined);
      setStatus(updated.status);
      showSuiSuccessSnackbar(translations.publishedSuccess);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : translations.publishFailed
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleClosePosition = async () => {
    if (!uuid.trim()) {
      return;
    }

    setIsUpdatingStatus(true);
    setErrorMessage('');

    try {
      const updated = await closePositionRequest(uuid, accessToken ?? undefined);
      setStatus(updated.status);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : translations.closeFailed
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDeletePosition = async () => {
    if (!uuid.trim()) {
      return;
    }

    const confirmed = window.confirm(translations.deleteConfirm);
    if (!confirmed) {
      return;
    }

    setIsUpdatingStatus(true);
    setErrorMessage('');

    try {
      await deletePositionRequest(uuid, accessToken ?? undefined);
      navigate('/positions', { replace: true });
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : translations.deleteFailed
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <SoftBox mb={2}>
                  <SoftTypography variant="h5" fontWeight="medium">
                    {translations.editTitle}
                  </SoftTypography>
                  <SoftTypography variant="button" color="text" fontWeight="regular">
                    {translations.editDescription}
                  </SoftTypography>
                </SoftBox>
                {isLoading ? (
                  <SoftTypography variant="button" color="text">
                    {translations.loading}
                  </SoftTypography>
                ) : (
                  <SoftBox component="form" onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                      <SoftTypography variant="button" fontWeight="medium" color="text">
                        {translations.statusLabel}: {status}
                      </SoftTypography>
                      <SoftInput
                        placeholder={translations.titlePlaceholder}
                        value={title}
                        onChange={(inputEvent) => setTitle(inputEvent.target.value)}
                      />
                      <SoftInput
                        placeholder={translations.descriptionPlaceholder}
                        multiline
                        rows={3}
                        value={description}
                        onChange={(inputEvent) => setDescription(inputEvent.target.value)}
                      />
                      <GooglePlacesAutocomplete
                        placeholder={translations.locationPlaceholder}
                        value={location}
                        onChange={setLocation}
                        onPlaceSelect={(place) => {
                          setLocationPlaceId(place?.placeId ?? null);
                          setLocationCoordinates(place?.coordinates ?? null);
                        }}
                        disabled={isSubmitting || isLoading}
                        restrictToEstablishments
                        regionContext={eventRegion}
                      />
                      <FormControl fullWidth>
                        <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SoftTypography component="label" variant="caption" fontWeight="bold">
                            {translations.budgetCurrencyLabel}
                          </SoftTypography>
                        </SoftBox>
                        <Select
                          input={<SoftInput />}
                          value={budgetCurrency}
                          onChange={(inputEvent) =>
                            setBudgetCurrency(
                              inputEvent.target.value as PositionBudgetCurrency
                            )
                          }
                        >
                          {CURRENCY_OPTIONS.map((currencyOption) => (
                            <MenuItem key={currencyOption.value} value={currencyOption.value}>
                              {currencyOption.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <SoftBox
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                        gap={2}
                      >
                        <SoftInput
                          type="number"
                          placeholder={translations.budgetMinPlaceholder}
                          value={budgetMinInput}
                          onChange={(inputEvent) => setBudgetMinInput(inputEvent.target.value)}
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                        <SoftInput
                          type="number"
                          placeholder={translations.budgetMaxPlaceholder}
                          value={budgetMaxInput}
                          onChange={(inputEvent) => setBudgetMaxInput(inputEvent.target.value)}
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      </SoftBox>
                      <SoftBox
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                        gap={2}
                      >
                        <SoftInput
                          type="datetime-local"
                          value={startsAtInput}
                          onChange={(inputEvent) =>
                            handleStartsAtInputChange(inputEvent.target.value)
                          }
                        />
                        <SoftInput
                          type="datetime-local"
                          value={endsAtInput}
                          onChange={(inputEvent) => setEndsAtInput(inputEvent.target.value)}
                        />
                      </SoftBox>
                      <SoftBox
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                        gap={2}
                      >
                        <CategorySelect
                          accessToken={accessToken ?? null}
                          value={categories}
                          onChange={setCategories}
                          disabled={isSubmitting}
                        />
                        <SkillsAutocomplete
                          accessToken={accessToken ?? null}
                          value={skills}
                          onChange={setSkills}
                          disabled={isSubmitting}
                        />
                      </SoftBox>
                      <SoftBox display="flex" gap={1} flexWrap="wrap">
                        <SoftButton
                          type="submit"
                          variant="gradient"
                          color="info"
                          disabled={isSubmitting || !title.trim() || isLoading}
                        >
                          {isSubmitting ? translations.saving : translations.saveChanges}
                        </SoftButton>
                        <SoftButton
                          type="button"
                          variant="outlined"
                          color="secondary"
                          onClick={() => navigate('/positions')}
                          disabled={isSubmitting}
                        >
                          {translations.cancel}
                        </SoftButton>
                        {status === 'DRAFT' ? (
                          <SoftButton
                            type="button"
                            variant="gradient"
                            color="success"
                            onClick={handlePublish}
                            disabled={isSubmitting || isUpdatingStatus}
                          >
                            {isUpdatingStatus ? translations.publishing : translations.publish}
                          </SoftButton>
                        ) : null}
                        {status !== 'CLOSED' ? (
                          <SoftButton
                            type="button"
                            variant="outlined"
                            color="error"
                            onClick={handleClosePosition}
                            disabled={isSubmitting || isUpdatingStatus}
                          >
                            {isUpdatingStatus ? translations.closing : translations.close}
                          </SoftButton>
                        ) : null}
                        <SoftButton
                          type="button"
                          variant="outlined"
                          color="error"
                          onClick={handleDeletePosition}
                          disabled={isSubmitting || isUpdatingStatus}
                        >
                          {isUpdatingStatus ? translations.deleting : translations.delete}
                        </SoftButton>
                      </SoftBox>
                    </Stack>
                  </SoftBox>
                )}
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditPosition;
